/**
 * Chemicals Form Dialog
 * 5-step stepper form for creating/editing chemical (raw material) catalog entries.
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../../supabaseClient';
import {
  Box, Typography, Button, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  alpha, useTheme, InputAdornment, Paper, Stepper, Step, StepLabel, StepContent,
  StepConnector, stepConnectorClasses, styled,
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import {
  Check as CheckIcon, Info as InfoIcon, AttachMoney as AttachMoneyIcon,
  Science as ScienceIcon, Search as SearchIcon, Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main } },
  [`&.${stepConnectorClasses.completed}`]: { [`& .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main } },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderLeftWidth: 3, borderRadius: 1, marginLeft: 11,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
  display: 'flex', height: 22, alignItems: 'center',
  ...(ownerState.active && { color: theme.palette.primary.main }),
  '& .QontoStepIcon-completedIcon': { color: theme.palette.primary.main, zIndex: 1, fontSize: 18 },
  '& .QontoStepIcon-circle': { width: 8, height: 8, borderRadius: '50%', backgroundColor: 'currentColor' },
}));

function QontoStepIcon({ active, completed, className }) {
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? <CheckIcon className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
    </QontoStepIconRoot>
  );
}

const UNIT_TYPES = ['weight', 'volume', 'drums', 'boxes', 'cases', 'bags', 'pallets', 'totes', 'skids', 'units', 'custom'];
const STD_UNIT_TYPES = UNIT_TYPES.filter(t => t !== 'custom');
const UNIT_MEASURES = ['g', 'kg', 'lbs', 'oz', 'ml', 'L', 'gal'];
const UNIT_MEASURE_LABELS = { g: 'g (grams)', kg: 'kg (kilograms)', lbs: 'lbs (pounds)', oz: 'oz (ounces)', ml: 'mL (milliliters)', L: 'L (liters)', gal: 'gal (gallons)' };
const MATERIAL_TYPES = ['Liquid', 'Powder', 'Solid', 'Granules', 'Paste', 'Gel', 'Oil', 'Wax'];
const HAZARD_CLASSIFICATIONS = ['No Classification', 'Flammable', 'Corrosive', 'Toxic', 'Irritant', 'Oxidizer'];

const BLANK = {
  name: '', sku: '', description: '', category: '', material_type: '', custom_material_type: '', form: '', color: '',
  primary_unit_type: 'drums', custom_unit_type: '', primary_unit_quantity: 1, primary_unit_measure: 'kg',
  density_g_ml: '', cas_number: '', storage_temp_min: '', storage_temp_max: '',
  storage_humidity_max: '', hazard_classification: '', custom_hazard_classification: '', storage_requirements: '',
  supplier_id: '', unit_cost: '', moq: '', lead_time_days: '', notes: ''
};

export default function ChemicalsFormDialog({ open, onClose, editingItem, companyId, suppliers = [] }) {
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [pendingSupplier, setPendingSupplier] = useState(null);
  const [form, setForm] = useState(BLANK);

  useEffect(() => {
    if (!open) return;
    if (editingItem) {
      const unitType = editingItem.primary_unit_type || 'drums';
      const isCustomUnit = !STD_UNIT_TYPES.includes(unitType);
      const matType = editingItem.material_type || '';
      const isCustomMat = matType && !MATERIAL_TYPES.includes(matType);
      const hazard = editingItem.hazard_classification || '';
      const isCustomHazard = hazard && !HAZARD_CLASSIFICATIONS.includes(hazard);
      setForm({
        name: editingItem.name || '', sku: editingItem.sku || '',
        description: editingItem.description || '', category: editingItem.category || '',
        material_type: isCustomMat ? 'custom' : matType,
        custom_material_type: isCustomMat ? matType : '',
        form: editingItem.form || '', color: editingItem.color || '',
        primary_unit_type: isCustomUnit ? 'custom' : unitType,
        custom_unit_type: isCustomUnit ? unitType : '',
        primary_unit_quantity: editingItem.primary_unit_quantity || 1,
        primary_unit_measure: editingItem.primary_unit_measure || 'kg',
        density_g_ml: editingItem.density_g_ml ?? '',
        cas_number: editingItem.cas_number || '',
        storage_temp_min: editingItem.storage_temp_min ?? '',
        storage_temp_max: editingItem.storage_temp_max ?? '',
        storage_humidity_max: editingItem.storage_humidity_max ?? '',
        hazard_classification: isCustomHazard ? 'custom' : hazard,
        custom_hazard_classification: isCustomHazard ? hazard : '',
        storage_requirements: editingItem.storage_requirements || '',
        supplier_id: editingItem.supplier_id || '',
        unit_cost: editingItem.unit_cost ?? '',
        moq: editingItem.moq ?? '',
        lead_time_days: editingItem.lead_time_days ?? '',
        notes: editingItem.notes || ''
      });
    } else {
      setForm(BLANK);
    }
    setStep(0);
    setError('');
  }, [open, editingItem]);

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const go = (n) => () => { setError(''); setStep(n); };

  const next1 = () => {
    setError('');
    if (!form.name.trim() || !form.sku.trim()) { setError('Chemical Name and SKU are required'); return; }
    setStep(1);
  };

  const next2 = () => {
    setError('');
    const qty = parseFloat(form.primary_unit_quantity);
    if (!form.primary_unit_type) { setError('Unit type is required'); return; }
    if (form.primary_unit_type === 'custom' && !form.custom_unit_type?.trim()) { setError('Custom unit name is required'); return; }
    if (!qty || qty <= 0) { setError('Unit quantity must be a positive number'); return; }
    if (!form.primary_unit_measure) { setError('Unit measure is required'); return; }
    setStep(2);
  };

  const next3 = () => {
    setError('');
    if (!form.density_g_ml && form.density_g_ml !== 0) { setError('Density is required'); return; }
    setStep(3);
  };

  const next4 = () => {
    setError('');
    if (!form.unit_cost) { setError('Unit cost is required'); return; }
    setStep(4);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const finalUnitType = form.primary_unit_type === 'custom' ? form.custom_unit_type.trim() : form.primary_unit_type;
      const finalMaterialType = form.material_type === 'custom' ? form.custom_material_type.trim() : form.material_type;
      const finalHazard = form.hazard_classification === 'custom' ? form.custom_hazard_classification.trim() : form.hazard_classification;
      const payload = {
        company_id: companyId, name: form.name, sku: form.sku,
        description: form.description || null, category: form.category || null,
        material_type: finalMaterialType || null, form: form.form || null, color: form.color || null,
        primary_unit_type: finalUnitType,
        primary_unit_quantity: parseFloat(form.primary_unit_quantity) || 1,
        primary_unit_measure: form.primary_unit_measure,
        density_g_ml: form.density_g_ml !== '' ? parseFloat(form.density_g_ml) : null,
        cas_number: form.cas_number || null,
        storage_temp_min: form.storage_temp_min !== '' ? parseFloat(form.storage_temp_min) : null,
        storage_temp_max: form.storage_temp_max !== '' ? parseFloat(form.storage_temp_max) : null,
        storage_humidity_max: form.storage_humidity_max !== '' ? parseFloat(form.storage_humidity_max) : null,
        hazard_classification: finalHazard || null,
        storage_requirements: form.storage_requirements || null,
        supplier_id: form.supplier_id || null,
        unit_cost: form.unit_cost !== '' ? parseFloat(form.unit_cost) : null,
        moq: form.moq !== '' ? parseInt(form.moq) : null,
        lead_time_days: form.lead_time_days !== '' ? parseInt(form.lead_time_days) : null,
        notes: form.notes || null, updated_at: new Date().toISOString()
      };
      if (editingItem?.id) {
        const { error } = await supabase.from('raw_materials').update(payload).eq('id', editingItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('raw_materials').insert([payload]);
        if (error) throw error;
      }
      onClose();
    } catch (err) { setError(err.message); }
  };

  const filteredSuppliers = supplierSearch
    ? suppliers.filter(s => s.name.toLowerCase().includes(supplierSearch.toLowerCase()))
    : suppliers;

  const tfSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, backgroundColor: 'white',
      '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3) },
      '&:hover fieldset': { borderColor: theme.palette.primary.main },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };
  const selSx = {
    borderRadius: 2, backgroundColor: 'white',
    '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3) },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
  };
  const labelSx = (n) => ({ '& .MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: step === n ? 500 : 400, color: step === n ? 'primary.main' : 'text.secondary' } });
  const paperSx = { borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'white', mt: 2, mb: 3 };

  // Density conversion: kg or g mass → volume in L
  const unitTypeDisplay = form.primary_unit_type === 'custom' ? (form.custom_unit_type || 'unit') : form.primary_unit_type;
  const densityInfo = (() => {
    const q = parseFloat(form.primary_unit_quantity), d = parseFloat(form.density_g_ml), m = form.primary_unit_measure;
    if (!q || !d || d <= 0) return null;
    let volL = m === 'kg' ? q / d : m === 'g' ? q / (d * 1000) : m === 'lbs' ? (q * 0.453592) / d : m === 'oz' ? (q * 0.0283495) / d : null;
    if (volL === null) return null;
    return `${q}${m} per ${unitTypeDisplay} ≈ ${volL.toFixed(3)}L per ${unitTypeDisplay} (density: ${d}g/mL)`;
  })();

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: 'white', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ p: 4, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>{editingItem ? 'Edit Chemical' : 'Create New Chemical'}</Typography>
          <IconButton onClick={onClose} sx={{ background: 'rgba(255,255,255,0.8)', borderRadius: 2 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}
          <Box sx={{ px: 3, pt: 3 }}>
            <Stepper activeStep={step} orientation="vertical" connector={<QontoConnector />}>

              {/* Step 1: Basic Information */}
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={labelSx(0)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InfoIcon sx={{ fontSize: 20 }} />Basic Information</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={paperSx}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Chemical Name *" value={form.name} onChange={set('name')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="SKU *" value={form.sku} onChange={set('sku')} sx={tfSx} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={set('description')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Category" value={form.category} onChange={set('category')} placeholder="e.g., Essential Oils" sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Material Type</InputLabel>
                            <Select value={form.material_type} onChange={set('material_type')} label="Material Type" sx={selSx}>
                              <MenuItem value=""><em>None</em></MenuItem>
                              {MATERIAL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                              <MenuItem value="custom">Custom...</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {form.material_type === 'custom' && (
                          <Grid item xs={12} md={4}><TextField fullWidth label="Custom Material Type" value={form.custom_material_type} onChange={set('custom_material_type')} placeholder="e.g., Emulsion" sx={tfSx} /></Grid>
                        )}
                        <Grid item xs={12} md={2}><TextField fullWidth label="Form" value={form.form} onChange={set('form')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={2}><TextField fullWidth label="Color" value={form.color} onChange={set('color')} sx={tfSx} /></Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={next1} sx={{ borderRadius: 2 }}>Next: Unit Configuration</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              {/* Step 2: Unit Configuration */}
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={labelSx(1)}>Unit Configuration</StepLabel>
                <StepContent>
                  <Paper sx={paperSx}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={5}>
                          <FormControl fullWidth>
                            <InputLabel>Primary Unit Type *</InputLabel>
                            <Select value={form.primary_unit_type} onChange={set('primary_unit_type')} label="Primary Unit Type *" sx={selSx}
                              renderValue={(v) => v === 'custom' ? 'Custom...' : v.charAt(0).toUpperCase() + v.slice(1)}>
                              {UNIT_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t === 'custom' ? 'Custom...' : t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {form.primary_unit_type === 'custom' && (
                          <Grid item xs={12} md={4}><TextField fullWidth label="Custom Unit Name *" value={form.custom_unit_type} onChange={set('custom_unit_type')} placeholder="e.g., containers" sx={tfSx} /></Grid>
                        )}
                        <Grid item xs={12} md={4}><TextField fullWidth label="Primary Unit Quantity *" type="number" value={form.primary_unit_quantity} onChange={set('primary_unit_quantity')} inputProps={{ min: 0, step: 0.001 }} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth>
                            <InputLabel>Primary Unit Measure *</InputLabel>
                            <Select value={form.primary_unit_measure} onChange={set('primary_unit_measure')} label="Primary Unit Measure *" sx={selSx}>
                              {UNIT_MEASURES.map(m => <MenuItem key={m} value={m}>{UNIT_MEASURE_LABELS[m] || m}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={go(0)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={next2} sx={{ borderRadius: 2 }}>Next: Physical Properties</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              {/* Step 3: Physical Properties */}
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={labelSx(2)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ScienceIcon sx={{ fontSize: 20 }} />Physical Properties</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={paperSx}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Density (g/mL) *" type="number" value={form.density_g_ml} onChange={set('density_g_ml')} inputProps={{ step: 0.001 }} sx={tfSx} /></Grid>
                        {densityInfo && (
                          <Grid item xs={12}>
                            <Paper sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.06), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, borderRadius: 2 }}>
                              <Typography variant="caption" color="info.main">{densityInfo}</Typography>
                            </Paper>
                          </Grid>
                        )}
                        <Grid item xs={12} md={4}><TextField fullWidth label="CAS Number" value={form.cas_number} onChange={set('cas_number')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Hazard Classification</InputLabel>
                            <Select value={form.hazard_classification} onChange={set('hazard_classification')} label="Hazard Classification" sx={selSx}>
                              <MenuItem value=""><em>None</em></MenuItem>
                              {HAZARD_CLASSIFICATIONS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                              <MenuItem value="custom">Custom...</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {form.hazard_classification === 'custom' && (
                          <Grid item xs={12} md={4}><TextField fullWidth label="Custom Hazard Classification" value={form.custom_hazard_classification} onChange={set('custom_hazard_classification')} sx={tfSx} /></Grid>
                        )}
                        <Grid item xs={12} md={4}><TextField fullWidth label="Storage Temp Min (°C)" type="number" value={form.storage_temp_min} onChange={set('storage_temp_min')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Storage Temp Max (°C)" type="number" value={form.storage_temp_max} onChange={set('storage_temp_max')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Max Humidity (%)" type="number" value={form.storage_humidity_max} onChange={set('storage_humidity_max')} sx={tfSx} /></Grid>
                        <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Storage Requirements" value={form.storage_requirements} onChange={set('storage_requirements')} sx={tfSx} /></Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={go(1)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={next3} sx={{ borderRadius: 2 }}>Next: Vendor & Pricing</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              {/* Step 4: Vendor & Pricing */}
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={labelSx(3)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AttachMoneyIcon sx={{ fontSize: 20 }} />Vendor & Pricing</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={paperSx}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>Vendor</Typography>
                          <Button fullWidth variant="outlined" onClick={() => setSupplierOpen(true)}
                            sx={{ height: 56, borderRadius: 3, justifyContent: 'space-between', textTransform: 'none', bgcolor: 'white', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, color: form.supplier_id ? 'text.primary' : 'text.secondary', fontWeight: 500, px: 2 }}>
                            <Typography sx={{ fontSize: '1rem' }}>{form.supplier_id ? suppliers.find(s => s.id === form.supplier_id)?.name || 'Select Vendor' : 'Select Vendor'}</Typography>
                            <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
                          </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField fullWidth label={`Unit Cost per ${form.primary_unit_measure || 'unit'} *`} type="number" value={form.unit_cost} onChange={set('unit_cost')}
                            placeholder={`Cost per ${form.primary_unit_measure || 'unit'}`}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={tfSx} />
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="MOQ" type="number" value={form.moq} onChange={set('moq')} sx={tfSx} /></Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth label="Lead Time (days)" type="number" value={form.lead_time_days} onChange={set('lead_time_days')} sx={tfSx} /></Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={go(2)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={next4} sx={{ borderRadius: 2 }}>Next: Additional Info</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              {/* Step 5: Additional Information */}
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={labelSx(4)}>Additional Information (Optional)</StepLabel>
                <StepContent>
                  <Paper sx={paperSx}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Notes" value={form.notes} onChange={set('notes')} sx={tfSx} /></Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={go(3)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 2 }}>{editingItem ? 'Update Chemical' : 'Create Chemical'}</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

            </Stepper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Supplier Selection Dialog */}
      <Dialog open={supplierOpen} onClose={() => setSupplierOpen(false)} fullWidth maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 4, height: '80vh', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` } }}>
        <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Select Vendor</Typography>
          <IconButton onClick={() => setSupplierOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField fullWidth placeholder="Search vendors..." value={supplierSearch} onChange={(e) => setSupplierSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment>, sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white' } }}
              sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }} />
          </Box>
          <Box sx={{ overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map(s => (
                  <TableRow key={s.id} hover sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' }, '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 2, pl: 3, pr: 3 } }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{s.name}</Typography></TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => setPendingSupplier(s)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', px: 2, bgcolor: pendingSupplier?.id === s.id ? 'black' : 'white', color: pendingSupplier?.id === s.id ? 'white' : 'text.primary', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, background: alpha(theme.palette.grey[50], 0.5) }}>
          <Button onClick={() => setSupplierOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" onClick={() => { if (pendingSupplier) setForm(p => ({ ...p, supplier_id: pendingSupplier.id })); setSupplierOpen(false); setSupplierSearch(''); setPendingSupplier(null); }}
            sx={{ borderRadius: 2, px: 3, bgcolor: 'black', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
            Select Vendor
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
